import React, { type ComponentProps, forwardRef } from 'react';
import type { TextInstance } from 'react-native';
import { StyleSheet } from 'react-native';

import { TYPE_CONFIG, WEIGHT_MAP } from '@garnish/constants';

import { useResponsive } from '../../../hooks';
import { Text } from '../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const DisplayText = forwardRef<TextInstance, DisplayTextProps>((props, ref) => {
  const {
    children,
    sizeMatch = DISPLAY_TEXT_DEFAULT_SIZE_MATCH,
    bold,
    style,
    ...restProps
  } = props;

  const { match } = useResponsive();

  const fontSize = match(sizeMatch);
  const textStyles = DISPLAY_TEXT_STYLES[fontSize] ?? DISPLAY_TEXT_DEFAULT_FONT_STYLES;
  const boldTextStyles = bold ? styles.boldText : undefined;
  const allStyles = [textStyles, boldTextStyles, style];

  return (
    <Text ref={ref} style={allStyles} bold={bold} {...restProps}>
      {children}
    </Text>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const DISPLAY_TEXT_STYLES = TYPE_CONFIG.DISPLAY;

const DISPLAY_TEXT_DEFAULT_SIZE_MATCH: DisplayTextFontSize[] = ['32', '48'];
const DISPLAY_TEXT_DEFAULT_FONT_SIZE: DisplayTextFontSize = '48';

const DISPLAY_TEXT_DEFAULT_FONT_STYLES = DISPLAY_TEXT_STYLES[DISPLAY_TEXT_DEFAULT_FONT_SIZE];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  boldText: {
    fontWeight: WEIGHT_MAP.bold,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type DisplayTextProps = Readonly<{
  sizeMatch?: DisplayTextFontSize[];
}> &
  ComponentProps<typeof Text>;

type DisplayTextFontSize = keyof typeof DISPLAY_TEXT_STYLES;
